import React, { useMemo } from 'react';
import {
  SidePanelProfilePetParentSettingFragment,
  SurveyType,
  useHasDeviceThatCanRecievePushQuery,
  useUpdatePetParentSettingMutation,
} from 'shared/types/graphql';
import { Flex, HStack, Switch, Text, Checkbox, Box, VStack, Tooltip } from '@televet/kibble-ui';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import SidePanelSurveyPopover from './SidePanelSurveyPopover';
import { GraphQLFetchPolicies } from 'shared/enums';

interface SidePanelContactOptionsProps {
  petParentSetting: SidePanelProfilePetParentSettingFragment | undefined | null;
  petParentId: string;
}

const SidePanelContactOptions = ({ petParentSetting, petParentId }: SidePanelContactOptionsProps): JSX.Element => {
  const [updatePetParentSetting] = useUpdatePetParentSettingMutation();
  const { isFeatureEnabled } = useFeatureFlag();

  const {
    id: petParentSettingId,
    smsNotifications,
    smsOptedOutByPetParent,
    emailNotifications,
    emailOptedOutByPetParent,
    sendSurvey,
    pushNotifications,
  } = petParentSetting || {};

  const { data: deviceData } = useHasDeviceThatCanRecievePushQuery({
    variables: {
      clinicPetParentIds: petParentId,
    },
    skip: !petParentId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const hasPushEnabledDevice = useMemo(
    () => !!deviceData?.findFirstClinicPetParentDevice && pushNotifications,
    [deviceData?.findFirstClinicPetParentDevice, pushNotifications],
  );

  const smsToggle = (): void => {
    if (!smsOptedOutByPetParent) {
      updatePetParentSetting({
        variables: {
          data: {
            smsNotifications: !smsNotifications,
          },
          where: {
            id: petParentSettingId,
          },
        },
      });
    }
  };

  const emailToggle = (): void => {
    if (!emailOptedOutByPetParent) {
      updatePetParentSetting({
        variables: {
          data: {
            emailNotifications: !emailNotifications,
          },
          where: {
            id: petParentSettingId,
          },
        },
      });
    }
  };

  const surveyType = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.NpsSurvey) ? SurveyType.Nps : SurveyType.Csat;
  }, [isFeatureEnabled]);

  const surveyToggle = (): void => {
    updatePetParentSetting({
      variables: {
        data: {
          sendSurvey: !sendSurvey,
        },
        where: {
          id: petParentSettingId,
        },
      },
    });
  };

  return (
    <VStack
      className="PetParentProfile__ContactOptions"
      justify="space-between"
      align="flex-start"
      pt={2}
      px={5}
      pb={5}
    >
      <Text size="sm" fontWeight="bold">
        Contact Options
      </Text>
      <HStack spacing={3}>
        <Tooltip
          titleCaseLabel={false}
          label={
            smsOptedOutByPetParent ? 'This option is disabled because the client turned it off.' : 'Text Notification'
          }
        >
          <Box className="ContactOptions__Text">
            <Checkbox isChecked={smsNotifications} isDisabled={!!smsOptedOutByPetParent} onChange={smsToggle}>
              Text
            </Checkbox>
          </Box>
        </Tooltip>
        <Tooltip
          titleCaseLabel={false}
          label={
            emailOptedOutByPetParent
              ? 'This option is disabled because the client turned it off.'
              : 'Email Notification'
          }
        >
          <Box className="ContactOptions__Email">
            <Checkbox isChecked={emailNotifications} isDisabled={!!emailOptedOutByPetParent} onChange={emailToggle}>
              Email
            </Checkbox>
          </Box>
        </Tooltip>
        <Tooltip
          titleCaseLabel={false}
          label={
            hasPushEnabledDevice
              ? 'This client has opted in to receiving mobile app notifications.'
              : 'This client has not opted in to receiving mobile app notifications.'
          }
        >
          <Box className="ContactOptions__Push">
            <Checkbox isChecked={hasPushEnabledDevice} isDisabled>
              Mobile App
            </Checkbox>
          </Box>
        </Tooltip>
      </HStack>
      <Flex pt={6} w="100%" justifyContent="space-between">
        <HStack>
          <Text display="inline-flex" size="sm" fontWeight="bold">
            {`Send ${surveyType} Survey`}
          </Text>
          <SidePanelSurveyPopover surveyType={surveyType} />
        </HStack>
        <Tooltip titleCaseLabel={false} label={`${surveyType} Survey`}>
          <HStack mx="10px">
            <Switch display="flex" isChecked={!!sendSurvey} onChange={surveyToggle} />
            <Text size="xs" cursor="default">
              {sendSurvey ? 'ON' : 'OFF'}
            </Text>
          </HStack>
        </Tooltip>
      </Flex>
    </VStack>
  );
};

export default SidePanelContactOptions;
